import { Link } from "react-router-dom";
import { ArrowRight, Eye, Target, BadgeCheck } from "lucide-react";
import { Reveal, Marquee } from "@/components/motion";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-32 pb-10">
        <Reveal>
          <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-emerald-700 mb-4">About Us</p>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance">
            Reliable medicines. <span className="text-emerald-800">Honest pricing.</span> Trusted trade.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed mt-8 max-w-3xl">
            Kanan Remedies is a WHO-GMP certified pharmaceutical company based in Rajasthan, committed to delivering high-quality, affordable healthcare solutions to licensed pharmacies, retailers, and hospitals across India. We believe that access to reliable, effective medicines should never be compromised by cost or availability, and this belief shapes every decision we make.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="flex flex-wrap gap-2.5 mt-8">
            {["WHO-GMP Certified", "Rajasthan, India", "B2B Only — Licensed Trade", "Pan-India Delivery"].map((chip) => (
              <span key={chip} className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs font-bold px-3.5 py-2 rounded-full">
                <BadgeCheck size={13} /> {chip}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      <Marquee items={["WHO-GMP Certified Formulations", "Affordable Healthcare Solutions", "Trusted by Licensed Pharmacies", "Pan-India Trade Supply"]} />

      <section className="max-w-[1100px] mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-5">
        <Reveal>
          <div className="rounded-3xl bg-emerald-900 p-8 sm:p-10 h-full" data-testid="about-vision-card">
            <span className="w-12 h-12 rounded-2xl bg-amber-400 text-emerald-950 flex items-center justify-center"><Eye size={22} /></span>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-white mt-6">Our Vision</h2>
            <p className="text-emerald-100/85 text-sm sm:text-base leading-relaxed mt-4">
              To become a trusted and dependable name in the pharmaceutical industry, recognized for consistent quality, ethical business practices, and a genuine commitment to improving patient outcomes across the country.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-10 h-full" data-testid="about-mission-card">
            <span className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center"><Target size={22} /></span>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mt-6">Our Mission</h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mt-4">
              To manufacture and supply pharmaceutical products that meet the highest standards of quality, safety, and efficacy, while building long-term, trust-based relationships with healthcare professionals and business partners. We aim to combine doctor-driven insights with patient-focused care, ensuring every product we deliver contributes to better health outcomes on the ground.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 sm:px-8 pb-10">
        <Reveal>
          <div className="rounded-3xl border border-emerald-100 bg-emerald-50/60 p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-bold text-xl sm:text-2xl text-slate-900">Partner with Kanan Remedies</h3>
              <p className="text-sm text-slate-600 mt-2 max-w-lg">Stock WHO-GMP certified formulations with trade pricing, box-wise schemes and a GST invoice on every order.</p>
            </div>
            <Link
              to="/shop"
              data-testid="about-shop-cta"
              className="group inline-flex items-center gap-2 bg-emerald-800 hover:bg-emerald-900 text-white px-8 py-4 rounded-xl font-bold text-sm shadow-md transition-all duration-300 active:scale-95 whitespace-nowrap"
            >
              Browse Catalog <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
